/**
 * Birth date selector with day, month, year dropdowns.
 * Better UX than calendar for selecting birth dates (no need to navigate month by month).
 * Outputs the same format as FieldDateInput: { date: Date } for compatibility.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import moment from 'moment';

import { ValidationError } from '../../components';

import css from './FieldBirthDateInput.module.css';

const getDaysInMonth = (year, month) => {
  const m = moment({ year, month: month - 1 });
  return m.daysInMonth();
};

const getYearRange = (minYearsAgo = 120, maxYearsAgo = 0) => {
  const currentYear = moment().year();
  const minYear = currentYear - minYearsAgo;
  const maxYear = currentYear - maxYearsAgo;
  const years = [];
  for (let y = maxYear; y >= minYear; y--) {
    years.push(y);
  }
  return years;
};

const BIRTH_DATE_YEARS_AGO = 120;

const FieldBirthDateInputComponent = props => {
  const { id, label, input, meta, disabled, intl, ...rest } = props;

  const { value, onChange, onBlur } = input;
  const date = value?.date;

  const [partial, setPartial] = useState(() =>
    date
      ? {
          day: moment(date).date(),
          month: moment(date).month() + 1,
          year: moment(date).year(),
        }
      : { day: '', month: '', year: '' }
  );

  useEffect(() => {
    if (date) {
      setPartial({
        day: moment(date).date(),
        month: moment(date).month() + 1,
        year: moment(date).year(),
      });
    } else {
      setPartial({ day: '', month: '', year: '' });
    }
  }, [value]);

  const years = useMemo(() => getYearRange(BIRTH_DATE_YEARS_AGO), []);
  const months = useMemo(
    () => moment.months().map((name, i) => ({ value: i + 1, label: name })),
    []
  );

  const handleChange = (field, val) => {
    const numVal = val ? parseInt(val, 10) : '';
    const newPartial = { ...partial, [field]: numVal };
    setPartial(newPartial);

    const { day: d, month: m, year: y } = newPartial;
    if (d && m && y) {
      const maxDay = getDaysInMonth(y, m);
      const clampedDay = Math.min(d, maxDay);
      onChange({ date: new Date(y, m - 1, clampedDay) });
    } else {
      onChange({ date: null });
    }
  };

  const maxDay = partial.year && partial.month ? getDaysInMonth(partial.year, partial.month) : 31;
  const days = useMemo(() => Array.from({ length: maxDay }, (_, i) => i + 1), [maxDay]);

  const { touched, invalid, error } = meta;
  const hasError = touched && invalid && error;

  const selectClasses = classNames(css.select, {
    [css.selectError]: hasError,
  });

  const classes = classNames(css.root, rest.className);

  const dayPlaceholder = intl?.formatMessage({ id: 'FieldBirthDateInput.dayPlaceholder' }) || 'Día';
  const monthPlaceholder =
    intl?.formatMessage({ id: 'FieldBirthDateInput.monthPlaceholder' }) || 'Mes';
  const yearPlaceholder =
    intl?.formatMessage({ id: 'FieldBirthDateInput.yearPlaceholder' }) || 'Año';

  return (
    <div className={classes}>
      {label ? (
        <label className={css.label} htmlFor={id ? `${id}-day` : undefined}>
          {label}
        </label>
      ) : null}
      <div className={css.selectsRow}>
        <select
          id={id ? `${id}-day` : undefined}
          className={selectClasses}
          value={partial.day || ''}
          onChange={e => handleChange('day', e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          aria-label={dayPlaceholder}
        >
          <option value="">{dayPlaceholder}</option>
          {days.map(d => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <select
          id={id ? `${id}-month` : undefined}
          className={selectClasses}
          value={partial.month || ''}
          onChange={e => handleChange('month', e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          aria-label={monthPlaceholder}
        >
          <option value="">{monthPlaceholder}</option>
          {months.map(m => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
        <select
          id={id ? `${id}-year` : undefined}
          className={selectClasses}
          value={partial.year || ''}
          onChange={e => handleChange('year', e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          aria-label={yearPlaceholder}
        >
          <option value="">{yearPlaceholder}</option>
          {years.map(y => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

const FieldBirthDateInput = props => {
  const { id, label, name, validate, disabled, ...rest } = props;

  return (
    <Field
      component={FieldBirthDateInputComponent}
      id={id}
      label={label}
      name={name}
      validate={validate}
      disabled={disabled}
      {...rest}
    />
  );
};

export default FieldBirthDateInput;
